import { useEffect, useRef, useState } from 'react';

import type { TLabel } from '@shared/types/label';

interface UseLabelActionOptions {
  initial: TLabel[];
  onSave: (updated: TLabel[]) => void;
}

export function useLabelAction({ initial, onSave }: UseLabelActionOptions) {
  const initialRef = useRef<TLabel[]>(initial || []);
  const [labels, setLabels] = useState<TLabel[]>(initial || []);

  useEffect(() => {
    setLabels(initial);
  }, [initial]);

  function isEqual(a: TLabel[], b: TLabel[]): boolean {
    if (a.length !== b.length) return false;
    const aIds = a.map((l) => l.id).sort();
    const bIds = b.map((l) => l.id).sort();
    return aIds.every((id, index) => id === bIds[index]);
  }

  function toggleLabel(newLabel: TLabel): TLabel[] {
    const exists = labels.some((l) => l.id === newLabel.id);
    const updated = exists
      ? labels.filter((l) => l.id !== newLabel.id)
      : [
          ...labels,
          {
            id: newLabel.id,
            name: newLabel.name,
            hexa_color: newLabel.hexa_color,
          },
        ];
    setLabels(updated);
    return updated;
  }

  function handleUpdateLabels(updated?: TLabel[]) {
    const toSave = updated || labels;
    if (!isEqual(toSave, initialRef.current)) {
      onSave(toSave);
      initialRef.current = toSave;
    }
  }

  return {
    labels,
    toggleLabel,
    handleUpdateLabels,
  };
}
